import React, { useState, useEffect } from 'react'
import Form from '../../../2_Molecules/Form/Form'
import Input from '../../../1_Atoms/Forms/Input/Input'
import Button from '../../../1_Atoms/Button/Button'
import { useForm } from '../../../../hooks/useForm'
import { languageController } from '../../../../controllers/language/languageController'
import { Svg } from '../../../1_Atoms/Svg/Svg'
import { handleRequest } from '../../../../utils/httpRequest'
import { CBB } from '../../../../utils/constants'
import AutoComplete from '../../../2_Molecules/AutoComplete/AutoComplete'
import parse from 'html-react-parser';
import { blockInvalidCharInNumberField } from '../../../../utils/helpers'

interface CurrentVehicleData {
  tradeInId: number | string
  tradeInMake: string
  tradeInModel: string
  tradeInYear: number
  tradeInTrim: string
  tradeInPriceLow: number
  tradeInPriceHigh: number
  searchTerm: string
  tradeInOdometer: number | string
}

interface SelectCurrentVehicleStepProps {
  handleNextStep: (data: { currentVehicleData: CurrentVehicleData }) => void
  topText?: string
}

interface VehicleFormData {
  vehicle: string
  kilometers: string
  [key: string]: string | number | boolean
}

interface AutocompleteResult {
  uvc: string | number
  description: string
}

interface CBBResponse {
  error_count?: number
  token?: string
  autocomplete?: AutocompleteResult[]
  used_vehicles?: {
    used_vehicle_list: Array<{
      make: string
      model: string
      model_year: number
      style: string
      canadian_tradein_low?: number
      canadian_tradein_high?: number
      adjusted_tradein_rough?: number
      adjusted_tradein_clean?: number
    }>
  }
}

declare global {
  interface Window {
    bnp_retailer_mode?: boolean
  }
}

const SelectCurrentVehicleStep: React.FC<SelectCurrentVehicleStepProps> = ({
  handleNextStep,
  topText
}) => {
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | number>(0);
  const [token, setToken] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [autocompleteData, setAutocompleteData] = useState<AutocompleteResult[]>([]);
  const [isAutoCompleteActive, setIsAutoCompleteActive] = useState<boolean>(false);
  const [selectedVehicleUVC, setSelectedVehicleUVC] = useState<string | number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const initState: VehicleFormData = {
    vehicle: '',
    kilometers: '',
  }

  const validations: Record<string, Array<'required' | 'name' | 'nameMaxMin' | 'postalcode' | 'email' | 'phone' | 'isAlphaNumeric' | 'vin' | 'odometer' | 'positive'>> = {
    vehicle: ['required'] as Array<'required' | 'name' | 'nameMaxMin' | 'postalcode' | 'email' | 'phone' | 'isAlphaNumeric' | 'vin' | 'odometer' | 'positive'>,
    kilometers: ['required', 'odometer', 'positive'] as Array<'required' | 'name' | 'nameMaxMin' | 'postalcode' | 'email' | 'phone' | 'isAlphaNumeric' | 'vin' | 'odometer' | 'positive'>,
  }

  useEffect(() => {
    const getCBBToken = async () => {
      try {
        const getTokenURL = `${CBB.GET_TOKEN_URL}?tokenminutes=${CBB.TOKEN_MINUTES}&customerid=${CBB.CUSTOMER_ID}&username=${CBB.USERNAME}&password=${CBB.PASSWORD}`;
        const res: CBBResponse = await handleRequest('get', getTokenURL)

        if (!res.error_count || res.error_count === 0) {
          if (res.token) {
            setToken(encodeURIComponent(res.token))
          }
        }
      } catch (err) {
        console.log(err)
      }
    }

    // Get CBB token.
    getCBBToken()
  }, [])

  const { handleSubmit, handleChange, data, errors, setData } = useForm({
    initState: initState as { [key: string]: string | number | boolean },
    validations,
    // Send the form data if validated
    onSubmit: async () => {
      try {
        if (!selectedVehicleUVC) return

        // Get full details of the selected vehicle with the kilometers.
        const tradeValuesURL = `${CBB.TRADE_VALUES_URL}/${selectedVehicleUVC}?na=na&template=12&customerid=${CBB.CUSTOMER_ID}&token=${token}&kilometres=${kilometers}`;
        const res: CBBResponse = await handleRequest('get', tradeValuesURL);

        const carData = res.used_vehicles!.used_vehicle_list[0];

        const priceLow = carData.canadian_tradein_low !== undefined ? carData.canadian_tradein_low : carData.adjusted_tradein_rough
        const priceHigh = carData.canadian_tradein_high !== undefined ? carData.canadian_tradein_high : carData.adjusted_tradein_clean
        const currentVehicleData: CurrentVehicleData = {
          tradeInId: selectedVehicleUVC,
          tradeInMake: carData.make,
          tradeInModel: carData.model,
          tradeInYear: carData.model_year,
          tradeInTrim: carData.style,
          tradeInPriceLow: priceLow!,
          tradeInPriceHigh: priceHigh!,
          searchTerm: searchTerm,
          tradeInOdometer: kilometersStr,
        }

        handleNextStep({ currentVehicleData })
      } catch (err) {
        console.log(err)
      }
    },
  })

  const { vehicle, kilometers } = data

  const vehicleStr = String(vehicle);
  const kilometersStr = String(kilometers);

  // Fetch vehicle data from CBB autocomplete API.
  const fetchVehicleAutoCompleteData = async (value: string) => {
    try {
      const autoCompleteURL = `${CBB.AUTO_COMPLETE_URL}?searchText=${encodeURIComponent(value)}&customerid=${CBB.CUSTOMER_ID}&token=${token}`;
      const res: CBBResponse = await handleRequest('get', autoCompleteURL)

      if (!res.error_count || res.error_count === 0) {
        if (res.autocomplete) {
          setAutocompleteData(res.autocomplete)
          setIsAutoCompleteActive(true)
        }
      }
    } catch (err: any) {
      if (err.response) {
        // Show error message (e.g. CBB token got expired)
        setFormError(languageController.getCopy('global', 'httpResponseError') + ' ' + err.response.status)
      }
    }
  }

  // Handle vehicle lookup when value is entered on input.
  const handleVehicleLookUp = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setData({ ...data, [name]: value, });

    clearTimeout(typingTimeout as NodeJS.Timeout);

    // Set a timeout to run after typing ends.
    const typingTimer = setTimeout(function () {
      if (value.length === 0) {
        setIsAutoCompleteActive(false)
        return
      }
      // Update autocomplete data 500ms after stops typing.
      fetchVehicleAutoCompleteData(value)
    }, 500);

    setTypingTimeout(typingTimer);
  }

  return (
    <div className="mz-select-current-vehicle-step-tie">
      <div className="mz-container">
        <div className="mz-select-current-vehicle-step-tie__top grid-inner-content">
          {
            topText && parse(topText)
          }
          {window.bnp_retailer_mode &&
            <>
              <h2>{languageController.getCopy('tradeInEstimator', 'retailerTitle')}</h2>
              <p>{languageController.getCopy('tradeInEstimator', 'retailerSubTitle')}</p>
            </>
          }
        </div>
        <div className="mz-select-current-vehicle-step-tie__form grid-inner-content">
          <Form
            type="select-current-vehicle"
            onSubmit={handleSubmit}
            isDark={false}
            noValidate={true}
            errorMessage={formError ?? undefined}
          >
            <AutoComplete
              isActive={isAutoCompleteActive}
              isSelected={(selectedVehicleUVC !== null)}
              onClick={(result: { id: string | number; label: string }) => {
                setSearchTerm(vehicleStr)
                setData({ ...data, vehicle: result.label })
                setIsAutoCompleteActive(false)
                setSelectedVehicleUVC(result.id)
              }}
              onReset={() => {
                setData({ ...data, vehicle: '' })
                setSelectedVehicleUVC(null)
              }}
              results={autocompleteData.map(data => ({
                id: data.uvc,
                label: data.description,
              }))}
              input={(
                <Input
                  type="text"
                  name="vehicle"
                  label={languageController.getFormCopy('label', 'vehicleLookUp')}
                  value={vehicleStr}
                  isRequired={true}
                  errorMessage={errors.vehicle}
                  onChange={handleVehicleLookUp}
                  maxLength={320}
                  disabled={(selectedVehicleUVC !== null)}
                />
              )}
            />
            <Input
              type="text"
              name="kilometers"
              label={languageController.getFormCopy('label', 'kilometers')}
              value={kilometersStr}
              isRequired={true}
              errorMessage={errors.kilometers}
              onKeyDown={blockInvalidCharInNumberField}
              onChange={(e) => {
                // Handle max length
                if (!isNaN(Number(e.target.value)) && e.target.value.length <= 7) {
                  handleChange(e)
                }
              }}
            />
            <Button
              type="submit"
              className="mz-btn--primary mz-btn--red"
              label={window.bnp_retailer_mode ? languageController.getCopy('tradeInEstimator', 'retailerButton') : languageController.getCopy('global', 'continue')}
              buttonIcon={window.bnp_retailer_mode ? null : <Svg type="chevron_small" size={0.8} colour="white" style={{ top: '1px' }} />}
              buttonIconPosition="right"
              disabled={(!selectedVehicleUVC || !kilometers)}
            />
          </Form>
          <div className="mz-select-current-vehicle-step-tie__disclaimer label label--tight text--grey">
            {languageController.getCopy('tradeInEstimator', 'step1Disclaimer')}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SelectCurrentVehicleStep
